import { lazy, Suspense, useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Mark } from "../components/Mark";
import box from "../assets/icons/box.svg";
import files from "../assets/icons/files.svg";
import hourglass from "../assets/icons/hourglass.svg";
import documentIcon from "../assets/icons/document.svg";
import { AuthRequestError, login, register } from "../lib/authActions";
import { apexUrlFor, brandInitials, type Branding } from "../lib/branding";
import { useBranding } from "../lib/brandingContext";
import type { WebSession } from "../lib/authSession";
import { useLanguage, type TranslateFn } from "../lib/i18n";
import { formatQuote } from "../lib/rfqList";
import { readWorkspaceCurrency } from "../lib/workspaceCurrency";
import { cx } from "../lib/cx";

const AuthPartStage = lazy(() => import("./auth/AuthPartStage"));

type Mode = "login" | "signup";

interface Props {
  onLoggedIn: (session: WebSession) => void;
  /** Tests open signup without a click. The product always starts on login. */
  initialMode?: Mode;
}

/**
 * Engine `hash_password` / `change_user_password` (`session_auth.py`) reject
 * anything shorter than this and enforce no other rules.
 */
const PASSWORD_MIN_LENGTH = 8;

/**
 * TODO: this repo and the engine have no published KVKK or terms URL.
 * Replace both before launch; the TODO segment keeps the placeholder obvious.
 */
const AUTH_KVKK_URL = "https://semanticad.ai/TODO-kvkk";
const AUTH_TERMS_URL = "https://semanticad.ai/TODO-terms";

const LEGAL_LINK = "font-semibold text-ds-teal underline underline-offset-2";

const STEP_KEYS = [
  "auth.step.drawing",
  "auth.step.features",
  "auth.step.cost",
  "auth.step.quote",
] as const;

/**
 * Light glass card from Documents / Production (`dsCard`):
 * blur 26, saturate 1.1, radius 26, card shadow, no border.
 */
const GLASS = cx(
  "rounded-ds-card border border-[#D5DBDF] bg-white shadow-ds-card",
);

/** Same pill as the design-system primary button, stretched for a form. */
const PRIMARY = cx(
  "m-0 inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-ds-pill border-0 px-5 py-0",
  "font-ds text-ds-item font-semibold",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px]",
  "disabled:cursor-wait disabled:opacity-65",
);

const FIELD = cx(
  "m-0 h-11 w-full rounded-ds-tile border-0 bg-ds-glass-strong px-4 py-0",
  "font-ds text-ds-body font-normal text-ds-ink outline-none",
  "placeholder:text-ds-ink-3",
  "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-ds-ink",
  "aria-[invalid=true]:outline aria-[invalid=true]:outline-2 aria-[invalid=true]:-outline-offset-1 aria-[invalid=true]:outline-danger",
  "[&:-webkit-autofill]:[-webkit-text-fill-color:var(--ds-ink)]",
  "[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#f7f7fa]",
);

const LABEL = "flex flex-col gap-1.5 font-ds text-ds-body font-semibold text-ds-ink";

const SWITCH_LINK =
  "m-0 cursor-pointer border-0 bg-transparent p-0 font-ds text-ds-body font-semibold text-ds-teal underline underline-offset-2";

/**
 * Documents page ground: cool grey, a white wash, and a quiet amber glow.
 * Kept inline so the sign-in surface does not add a stylesheet.
 */
const PAGE_BG =
  "radial-gradient(900px 500px at 78% -10%, rgba(255,255,255,.55), transparent 60%), radial-gradient(700px 500px at 0% 100%, rgba(241,194,50,.12), transparent 60%), linear-gradient(160deg, var(--ds-bg-a), var(--ds-bg-b))";

/**
 * Static product snapshot for the sign-in brand panel. Not live org data —
 * the session does not exist yet, so the cards illustrate the desk a shop
 * lands on after login (home KPIs + a costed drawing). The example badge
 * and caption stay outside `aria-hidden` so the figures are not read as
 * the visitor's account.
 */
const PREVIEW_KPIS = [
  { key: "parts", title: "auth.preview.parts", caption: "auth.preview.partsCap", value: "128", icon: box },
  { key: "boms", title: "auth.preview.boms", caption: "auth.preview.bomsCap", value: "24", icon: files },
  { key: "rfqs", title: "auth.preview.rfqs", caption: "auth.preview.rfqsCap", value: "9", icon: hourglass },
  { key: "orders", title: "auth.preview.orders", caption: "auth.preview.ordersCap", value: "4", icon: documentIcon },
] as const;

function PlateMark() {
  return (
    <svg className="block size-10" viewBox="0 0 48 48" aria-hidden="true">
      <rect x="7" y="11" width="34" height="26" rx="3" className="fill-ds-glass-strong stroke-ds-teal" strokeWidth="1.6" />
      <circle cx="14" cy="18" r="2.4" className="fill-ds-glass-strong stroke-ds-teal" strokeWidth="1.6" />
      <circle cx="34" cy="18" r="2.4" className="fill-ds-glass-strong stroke-ds-teal" strokeWidth="1.6" />
      <circle cx="14" cy="30" r="2.4" className="fill-ds-glass-strong stroke-ds-teal" strokeWidth="1.6" />
      <circle cx="34" cy="30" r="2.4" className="fill-ds-glass-strong stroke-ds-teal" strokeWidth="1.6" />
      <circle cx="24" cy="24" r="4" className="fill-ds-glass-strong stroke-ds-teal" strokeWidth="1.6" />
    </svg>
  );
}

/**
 * Brand lock-up for the sign-in panel: the org's logo (or an accent initials
 * tile) and display name on a workspace host, the SemantiCAD wordmark otherwise.
 */
function AuthBrand({ branding, t }: { branding: Branding | null; t: TranslateFn }) {
  const name = branding?.displayName;
  if (!branding || !name) {
    return (
      <span className="flex items-center gap-2 text-[16px] font-semibold whitespace-nowrap text-ds-ink">
        <Mark />
        <span>
          Semanti<b className="font-semibold text-ds-teal">CAD</b>
        </span>
      </span>
    );
  }
  return (
    <span className="flex min-w-0 items-center gap-2.5" data-testid="auth-org-brand">
      {branding.logoUrl ? (
        <img
          src={branding.logoUrl}
          alt={t("auth.orgLogoAlt", { name })}
          className="block h-8 w-auto max-w-[160px] shrink-0 object-contain"
          referrerPolicy="no-referrer"
          data-testid="auth-org-logo"
        />
      ) : (
        <span
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-[color:var(--org-accent,var(--ds-teal))] text-ds-item font-semibold text-[color:var(--org-accent-ink,#ffffff)]"
          aria-hidden="true"
        >
          {brandInitials(name)}
        </span>
      )}
      <span
        className="min-w-0 truncate text-[16px] font-semibold tracking-[-0.02em] text-ds-ink"
        data-testid="auth-org-name"
      >
        {name}
      </span>
    </span>
  );
}

function AuthPreview({ t, locale }: { t: TranslateFn; locale: "en" | "tr" }) {
  const sample = formatQuote(4180, readWorkspaceCurrency(), locale);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4" data-testid="auth-preview">
      <div className="flex items-start justify-between gap-3">
        <p className="m-0 min-w-0 text-ds-body font-normal leading-snug text-ds-ink-2" data-testid="auth-preview-sample">
          {t("auth.preview.sample")}
        </p>
        <span
          className="inline-flex shrink-0 items-center rounded-ds-pill border border-solid border-ds-hair bg-ds-glass-strong px-2.5 py-1 text-ds-meta font-semibold text-ds-ink"
          data-testid="auth-preview-example"
        >
          {t("auth.preview.example")}
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-between gap-4" aria-hidden="true">
        <div className="grid grid-cols-2 gap-2.5">
          {PREVIEW_KPIS.map((card) => (
            <article
              key={card.key}
              className="flex min-h-[84px] min-w-0 flex-col justify-between gap-2 rounded-ds-tile bg-ds-glass-strong px-3 py-2.5"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-ds-tile bg-ds-glass">
                  <img src={card.icon} alt="" className="block size-4" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <strong className="text-ds-item font-semibold tracking-[-0.02em] text-ds-ink">
                    {t(card.title)}
                  </strong>
                  <em className="text-ds-label font-normal not-italic leading-snug text-ds-ink-3">
                    {t(card.caption)}
                  </em>
                </span>
              </div>
              <b className="text-ds-kpi font-semibold leading-none tracking-[-0.03em] text-ds-ink tabular-nums">
                {card.value}
              </b>
            </article>
          ))}
        </div>

        <div
          className="relative h-[148px] overflow-hidden rounded-ds-tile bg-[radial-gradient(ellipse_at_50%_58%,#f7f8fb_0%,#e3e5ed_80%)] max-[640px]:hidden"
          data-testid="auth-part-stage"
        >
          <Suspense fallback={null}>
            <AuthPartStage />
          </Suspense>
        </div>

        <div className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3 rounded-ds-tile bg-ds-glass-strong px-3 py-2.5 max-[640px]:hidden">
          <PlateMark />
          <div className="min-w-0">
            <strong className="block truncate text-ds-item font-semibold tracking-[-0.02em] text-ds-ink">
              Plate-A12.dxf
            </strong>
            <span className="mt-0.5 block text-ds-meta font-normal text-ds-ink-3">{t("auth.preview.parsed")}</span>
          </div>
          <b
            className="whitespace-nowrap text-ds-card font-semibold tracking-[-0.02em] text-ds-ink tabular-nums"
            data-testid="auth-preview-cost"
          >
            {sample}
          </b>
        </div>
      </div>
    </div>
  );
}

/**
 * Words engine refusals the raw English detail would not explain: on a
 * workspace host (#85) login for another org's member is `403 org_mismatch`,
 * signup is refused with the same code, and an unknown host is `404
 * unknown_organization`. Everything else keeps the engine's message.
 */
export function authErrorMessage(
  err: unknown,
  mode: Mode,
  workspaceName: string | null,
  t: TranslateFn,
): string {
  if (err instanceof AuthRequestError) {
    if (err.code === "org_mismatch") {
      if (mode === "signup") return t("auth.orgSignupRefused");
      return workspaceName ? t("auth.orgMismatchNamed", { name: workspaceName }) : t("auth.orgMismatch");
    }
    if (err.code === "unknown_organization") return t("auth.unknownWorkspace");
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Sign-in and create-account on the light glass ground from Documents and
 * Production, with the sample workshop and the form as a matched pair.
 * "Forgot password?" is not offered — the engine has no reset endpoint. The
 * engine has one organization per account, so "Shop name" is a field.
 */
export function AuthPage({ onLoggedIn, initialMode = "login" }: Props) {
  const { locale, setLocale, t } = useLanguage();
  const { status: brandingStatus, branding, isOrgHost } = useBranding();
  // Self-serve signup creates a new org, so a workspace host never offers it
  // (the engine refuses it there too). Held back until branding answers.
  const signupAllowed = brandingStatus === "ready" && !isOrgHost;
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const accent = branding?.accentColor ?? null;
  const apexUrl = apexUrlFor(branding?.subdomain ?? null, window.location);

  useEffect(() => {
    document.body.classList.add("sc-active");
    return () => document.body.classList.remove("sc-active");
  }, []);

  useEffect(() => {
    if (isOrgHost && mode === "signup") {
      setMode("login");
      setConfirm("");
      setConsent(false);
    }
  }, [isOrgHost, mode]);

  function describeError(err: unknown): string {
    return authErrorMessage(err, mode, branding?.displayName ?? null, t);
  }

  const copy =
    mode === "login"
      ? {
          title: t("auth.login.title"),
          blurb: t("auth.login.blurb"),
          cta: t("auth.login.cta"),
          switch: t("auth.login.switch"),
          switchCta: t("auth.login.switchCta"),
        }
      : {
          title: t("auth.signup.title"),
          blurb: t("auth.signup.blurb"),
          cta: t("auth.signup.cta"),
          switch: t("auth.signup.switch"),
          switchCta: t("auth.signup.switchCta"),
        };
  const mismatch = mode === "signup" && confirm.length > 0 && confirm !== password;
  const passwordTooShort = mode === "signup" && password.length > 0 && password.length < PASSWORD_MIN_LENGTH;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (mode === "signup" && password.length < PASSWORD_MIN_LENGTH) {
      setError(t("auth.passwordTooShort"));
      return;
    }
    if (mode === "signup" && confirm !== password) {
      setError(t("auth.mismatch"));
      return;
    }
    if (mode === "signup" && !consent) {
      setError(t("auth.consentRequired"));
      return;
    }
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      const session =
        mode === "login"
          ? await login(email, password)
          : await register({ email, password, organizationName, displayName, consent });
      onLoggedIn(session);
    } catch (err) {
      setError(describeError(err));
      setErrorCode(err instanceof AuthRequestError ? (err.code ?? null) : null);
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next: Mode) {
    if (next === "signup" && !signupAllowed) return;
    setMode(next);
    setError(null);
    setErrorCode(null);
    setConfirm("");
    setConsent(false);
  }

  function eye(testId: string) {
    return (
      <button
        type="button"
        className="absolute top-0 right-1.5 bottom-0 my-auto inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-ds-pill border-0 bg-transparent p-0 text-ds-ink-3 hover:bg-ds-hover hover:text-ds-ink"
        aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
        aria-pressed={showPassword}
        data-testid={testId}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShowPassword((v) => !v)}
      >
        {showPassword ? (
          <EyeOff size={18} strokeWidth={1.75} aria-hidden="true" />
        ) : (
          <Eye size={18} strokeWidth={1.75} aria-hidden="true" />
        )}
      </button>
    );
  }

  return (
    <div
      data-theme="light"
      className="h-dvh overflow-x-hidden overflow-y-auto font-ds text-ds-ink"
      style={{ colorScheme: "light", backgroundImage: PAGE_BG }}
    >
      <div className="flex min-h-full items-center justify-center px-4 py-6 desk:px-8 desk:py-8">
        <div className="grid w-full max-w-[1120px] grid-cols-1 items-stretch gap-4 desk:grid-cols-2 desk:gap-5">
          <aside className={cx(GLASS, "flex min-w-0 flex-col gap-5 px-5 py-5 desk:px-6 desk:py-6")}>
            <div className="flex items-center" data-testid="auth-brand">
              <AuthBrand branding={branding} t={t} />
            </div>
            <AuthPreview t={t} locale={locale === "tr" ? "tr" : "en"} />
            <div className="mt-auto flex flex-col gap-1.5">
              <p className="m-0 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-ds-meta font-semibold text-ds-ink-3">
                {STEP_KEYS.map((key, i) => (
                  <span key={key} className="inline-flex items-center gap-1.5">
                    {i > 0 ? <i className="inline-block h-px w-3.5 bg-ds-hair" /> : null}
                    {t(key)}
                  </span>
                ))}
              </p>
              <p className="m-0 text-ds-card font-normal text-ds-ink-2">{t("auth.tagline")}</p>
            </div>
          </aside>

          <form
            className={cx(GLASS, "flex min-w-0 flex-col justify-center gap-3.5 px-5 py-5 desk:px-7 desk:py-6")}
            onSubmit={submit}
            data-testid="auth-form"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1
                  className="m-0 font-ds text-ds-page font-semibold tracking-[-0.02em] text-ds-teal"
                  data-testid="auth-title"
                >
                  {copy.title}
                </h1>
                <p className="mt-1.5 mb-0 text-ds-body font-normal leading-normal text-ds-ink-2">{copy.blurb}</p>
              </div>
              <div
                className="inline-flex shrink-0 rounded-ds-pill bg-ds-glass-strong p-0.5"
                role="group"
                aria-label={t("auth.language")}
                data-testid="auth-lang"
              >
                {(["en", "tr"] as const).map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={cx(
                      "m-0 h-8 min-w-9 cursor-pointer rounded-ds-pill border-0 px-2.5 py-0 font-ds text-ds-badge font-semibold",
                      locale === code
                        ? "bg-ds-ink text-ds-bg-a"
                        : "bg-transparent text-ds-ink-2 hover:bg-ds-hover",
                    )}
                    aria-pressed={locale === code}
                    data-testid={`auth-lang-${code}`}
                    onClick={() => setLocale(code)}
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {mode === "signup" ? (
              <label className={LABEL}>
                {t("auth.field.name")}
                <input
                  value={displayName}
                  autoComplete="name"
                  data-testid="auth-name"
                  className={FIELD}
                  onChange={(e) => setDisplayName(e.currentTarget.value)}
                />
              </label>
            ) : null}

            <label className={LABEL}>
              {t("auth.field.email")}
              <input
                type="email"
                value={email}
                required
                autoComplete="email"
                data-testid="auth-email"
                className={FIELD}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
            </label>

            {mode === "signup" ? (
              <label className={LABEL}>
                {t("auth.field.org")}
                <input
                  value={organizationName}
                  required
                  autoComplete="organization"
                  placeholder={t("auth.field.orgPh")}
                  data-testid="auth-org"
                  className={FIELD}
                  onChange={(e) => setOrganizationName(e.currentTarget.value)}
                />
              </label>
            ) : null}

            <label className={LABEL}>
              {mode === "login" ? t("auth.field.password") : t("auth.field.passwordCreate")}
              <span className="relative flex">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  required
                  minLength={mode === "signup" ? PASSWORD_MIN_LENGTH : undefined}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  aria-describedby={mode === "signup" ? "auth-password-rules" : undefined}
                  data-testid="auth-password"
                  className={cx(FIELD, "pr-11")}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                />
                {eye("auth-eye")}
              </span>
              {mode === "signup" ? (
                <span
                  id="auth-password-rules"
                  className={cx("text-ds-meta font-normal", passwordTooShort ? "text-danger" : "text-ds-ink-3")}
                  data-testid="auth-password-rules"
                >
                  {t("auth.passwordRules")}
                </span>
              ) : null}
            </label>

            {mode === "signup" ? (
              <label className={LABEL}>
                {t("auth.field.confirm")}
                <span className="relative flex">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirm}
                    required
                    autoComplete="new-password"
                    aria-invalid={mismatch}
                    data-testid="auth-confirm"
                    className={cx(FIELD, "pr-11")}
                    onChange={(e) => setConfirm(e.currentTarget.value)}
                  />
                  {eye("auth-confirm-eye")}
                </span>
                {mismatch ? (
                  <em className="text-ds-meta font-normal not-italic text-danger">{t("auth.mismatch")}</em>
                ) : null}
              </label>
            ) : null}

            {mode === "signup" ? (
              <div className="flex items-start gap-2.5">
                <input
                  id="auth-consent"
                  type="checkbox"
                  required
                  checked={consent}
                  data-testid="auth-consent"
                  className="m-0 size-4 shrink-0 translate-y-0.5 accent-ds-teal"
                  onChange={(e) => setConsent(e.currentTarget.checked)}
                />
                <label htmlFor="auth-consent" className="font-ds text-ds-body font-normal leading-[1.45] text-ds-ink-2">
                  {t("auth.consent.before")}
                  <a href={AUTH_KVKK_URL} className={LEGAL_LINK} target="_blank" rel="noopener noreferrer" data-testid="auth-kvkk">
                    {t("auth.consent.kvkk")}
                  </a>
                  {t("auth.consent.between")}
                  <a href={AUTH_TERMS_URL} className={LEGAL_LINK} target="_blank" rel="noopener noreferrer" data-testid="auth-terms">
                    {t("auth.consent.terms")}
                  </a>
                  {t("auth.consent.after")}
                </label>
              </div>
            ) : null}

            {error ? (
              <p
                className="m-0 rounded-ds-tile border border-danger bg-danger-wash px-3 py-2.5 text-ds-body font-normal text-danger"
                role="alert"
                data-testid="auth-error"
              >
                {error}
                {errorCode === "org_mismatch" && mode === "login" && apexUrl ? (
                  <>
                    {" "}
                    <a href={apexUrl} className="font-medium text-danger underline underline-offset-2" data-testid="auth-apex-link">
                      {t("auth.orgApexLink")}
                    </a>
                  </>
                ) : null}
              </p>
            ) : null}

            <button
              type="submit"
              className={cx(
                PRIMARY,
                "mt-1",
                accent
                  ? // Org accent (validated #rrggbb on :root) with its contrast-safe ink.
                    "bg-[color:var(--org-accent)] text-[color:var(--org-accent-ink)] focus-visible:outline-[color:var(--org-accent)]"
                  : "bg-ds-amber text-ds-on-amber shadow-ds-amber focus-visible:outline-ds-ink",
              )}
              disabled={busy || mismatch}
              data-testid="auth-submit"
            >
              {busy ? t("auth.busy") : copy.cta}
            </button>

            <p className="m-0 min-h-6 text-center text-ds-body font-normal text-ds-ink-2" data-testid="auth-switch">
              {mode === "login" && isOrgHost ? (
                <span className="text-ds-ink-3" data-testid="auth-invite-only">
                  {t("auth.orgInviteOnly")}
                </span>
              ) : mode === "login" && !signupAllowed ? null : mode === "login" ? (
                <>
                  {copy.switch}{" "}
                  <button type="button" className={SWITCH_LINK} data-testid="auth-to-signup" onClick={() => switchMode("signup")}>
                    {copy.switchCta}
                  </button>
                </>
              ) : (
                <>
                  {copy.switch}{" "}
                  <button type="button" className={SWITCH_LINK} data-testid="auth-to-login" onClick={() => switchMode("login")}>
                    {copy.switchCta}
                  </button>
                </>
              )}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
